import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Ellipsis, FileSpreadsheet, LoaderIcon } from 'lucide-react';
import { cn } from '@/utils/utils.ts';
import { TNotification } from '@/features/notifications/schemas/notifications.schemas.ts';
import { CSVColumn, useCSVExport } from '@/hooks/use-csv-export.ts';
import { useParams } from 'react-router';
import { useGetPlantEventsQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-events.ts';

export function EventsMoreSettingsPopover() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" className="cursor-pointer rounded-lg bg-gray-100 p-2">
          <Ellipsis />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="end" sideOffset={4}>
        <div className="space-y-1" role="menu">
          <ExportCSVButton />
          {/*<EventsFilterDialog />*/}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ExportCSVButton() {
  const { plantId } = useParams();
  const { data } = useGetPlantEventsQuery({ plantId });

  const events = data?.pages ?? [];

  const csvColumns: CSVColumn<TNotification>[] = useMemo(
    () => [
      {
        key: 'type',
        header: 'Type',
        accessor: (notification: TNotification) => {
          const type = notification.type;

          return type ? type?.charAt(0).toUpperCase() + type?.slice(1) : 'Unknown';
        },
      },
      {
        key: 'description',
        header: 'Description',
        accessor: (notification: TNotification) => notification.description,
      },
      {
        key: 'createdAt',
        header: 'Created At',
        accessor: (notification: TNotification) => notification.createdAt,
        formatter: (date: string | number | Date) => new Date(date).toLocaleString('ro-RO'),
      },
    ],
    []
  );

  const { downloadCSV, isExporting } = useCSVExport({
    data: events,
    columns: csvColumns,
    filename: `EMS_Events_${new Date().toLocaleDateString('ro-RO')}`,
    dateFormat: 'dd-MM-yyyy',
    locale: 'ro-RO',
  });

  return (
    <button
      onClick={() => downloadCSV()}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
        'text-left',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50'
      )}
      role="menuitem"
    >
      {isExporting ? (
        <LoaderIcon className="size-4 animate-spin text-gray-800" aria-hidden="true" />
      ) : (
        <FileSpreadsheet className="size-4" />
      )}
      {isExporting ? 'Exporting...' : 'Export as CSV'}
    </button>
  );
}
